import { ChevronLeftIcon, ChevronRightIcon, ExternalLinkIcon } from "lucide-react";
import type { KeyboardEvent } from "react";
import type { Picture } from "../../../../shared/api.ts";
import { Button } from "@/components/ui/button.tsx";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { domainOf } from "@/lib/utils.ts";

/**
 * One image the lightbox shows. Without an origin it is a picture of the good.
 * An upload has no page; for an image a source stored, `pageUrl` is the address it was read from.
 */
export type LightboxImage = Picture & { origin?: "picture" | "upload" | "source" };

/**
 * An image of the session, large, with its caption and a link to the page it came from.
 * When that page is unknown, it links to the address the image file was downloaded from.
 */
export function Lightbox({
  pictures,
  index,
  onIndexChange,
  onClose,
}: {
  pictures: LightboxImage[];
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const picture = index !== null ? pictures[index] : undefined;
  const total = pictures.length;
  const go = (delta: number) => {
    if (index === null || total === 0) return;
    onIndexChange((index + delta + total) % total);
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "ArrowRight") go(1);
    if (event.key === "ArrowLeft") go(-1);
  };

  return (
    <Dialog open={picture !== undefined} onOpenChange={(open) => !open && onClose()}>
      <DialogContent closeLabel={t.common.close} className="max-w-3xl gap-3 p-3 sm:p-4" onKeyDown={onKeyDown}>
        {picture ? (
          <>
            <DialogTitle className="sr-only">{picture.caption || t.panel.pictureAlt((index ?? 0) + 1)}</DialogTitle>
            <div className="relative flex items-center justify-center overflow-hidden rounded-lg border bg-[repeating-conic-gradient(var(--muted)_0%_25%,var(--card)_0%_50%)] bg-[length:16px_16px]">
              <img src={picture.url} alt={picture.caption || t.panel.pictureAlt((index ?? 0) + 1)} className="max-h-[68dvh] w-auto max-w-full object-contain" />
              {total > 1 ? (
                <>
                  <Button
                    variant="outline"
                    size="icon-sm"
                    className="absolute top-1/2 left-2 -translate-y-1/2 rounded-full bg-card/90 shadow"
                    aria-label={t.common.previous}
                    onClick={() => go(-1)}
                  >
                    <ChevronLeftIcon />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon-sm"
                    className="absolute top-1/2 right-2 -translate-y-1/2 rounded-full bg-card/90 shadow"
                    aria-label={t.common.next}
                    onClick={() => go(1)}
                  >
                    <ChevronRightIcon />
                  </Button>
                </>
              ) : null}
            </div>
            <div className="flex flex-col gap-1 px-1 pb-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
              <DialogDescription className="text-[13px] text-foreground">{picture.caption}</DialogDescription>
              <div className="flex shrink-0 items-center gap-3 text-[12.5px]">
                {total > 1 ? <span className="code-mono shrink-0 text-muted-foreground">{t.panel.pictureCounter((index ?? 0) + 1, total)}</span> : null}
                {picture.pageUrl ? (
                  <a href={picture.pageUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-primary hover:underline">
                    {picture.origin === "source" ? t.images.sourceLink : t.panel.pictureSource}: {domainOf(picture.pageUrl)}
                    <ExternalLinkIcon className="size-3.5" aria-hidden />
                  </a>
                ) : (
                  <span className="max-w-xs text-muted-foreground">
                    {picture.origin === "upload" ? t.images.uploadNote : t.panel.pictureNoSource}
                    {picture.origin !== "upload" && picture.imageUrl ? (
                      <>
                        {" "}
                        <a href={picture.imageUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-primary hover:underline">
                          {t.panel.pictureFile}: {domainOf(picture.imageUrl)}
                          <ExternalLinkIcon className="size-3.5" aria-hidden />
                        </a>
                      </>
                    ) : null}
                  </span>
                )}
              </div>
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
